// TOEFL iBT Mock 1 — Reading (2026 format, multistage adaptive)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, menus).
//   academic – Read an Academic Passage: ~200-word passage, 5 questions.
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

export const readingModules = {
  module1: [
    {
      id: "m1-words",
      type: "words",
      title: "Pollinators",
      text: "Bees are among the most important pollinators on the planet. As they move from flower to flower, they [[carry]] pollen that [[allows]] plants to [[produce]] seeds and fruit. [[Without]] this service, many [[crops]] would yield far [[less]] food. In recent [[decades]], however, bee [[populations]] have declined in [[several]] regions. [[Scientists]] blame habitat loss, pesticides, and disease.",
    },
    {
      id: "m1-daily1",
      type: "daily",
      title: "Pool Notice",
      text: `RIVERSIDE COMMUNITY POOL — SCHEDULE CHANGE

Starting Monday, June 9, the pool will open one hour later (7:00 a.m.) on weekdays while the new heating system is installed. Evening hours are not affected.

Lap swimming will be available only in lanes 1–3 until the work is complete, expected by June 20. Swim lessons will continue as scheduled.

Members who are unable to attend because of the change may request a one-week extension of their membership at the front desk.`,
      questions: [
        {
          question: "Why is the pool changing its morning hours?",
          options: [
            "Swim lessons have been moved to the morning.",
            "New equipment is being installed.",
            "Fewer staff are available on weekdays.",
            "The lanes are being repainted.",
          ],
          correct: 1,
        },
        {
          question: "What can members do if the change affects them?",
          options: [
            "Swim in any lane they choose.",
            "Get a refund for the month of June.",
            "Ask for extra time on their membership.",
            "Attend free evening swim lessons.",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m1-daily2",
      type: "daily",
      title: "Staff Email",
      text: `From: Dana Ruiz (Shift Manager)
To: All weekend staff
Subject: Saturday coverage

Hi everyone,

Marcus has a family event this Saturday and needs someone to cover his 2–8 p.m. shift at the register. If you can take it, please reply to me directly by Thursday at noon rather than arranging the swap between yourselves — I need to update the schedule in the system so that payroll is correct.

Whoever covers will get first choice of shifts on the next schedule.

Thanks,
Dana`,
      questions: [
        {
          question: "What is the main purpose of the email?",
          options: [
            "To announce a new payroll system",
            "To find a replacement for a coworker",
            "To invite staff to a family event",
            "To change the store's weekend hours",
          ],
          correct: 1,
        },
        {
          question: "Why does Dana want staff to contact her directly?",
          options: [
            "So the official schedule stays accurate",
            "Because Marcus is unavailable by email",
            "So she can choose the most experienced worker",
            "Because the deadline has already passed",
          ],
          correct: 0,
        },
        {
          question: "What is offered to the person who covers the shift?",
          options: [
            "Extra pay for the Saturday shift",
            "A day off the following week",
            "Priority when choosing future shifts",
            "An invitation to Marcus's event",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m1-academic",
      type: "academic",
      title: "The Domestication of Dogs",
      text: `Dogs were the first animals to be domesticated, but exactly when and how this happened remains a subject of debate. Genetic studies suggest that dogs and modern wolves descend from a common ancestor that lived somewhere between 15,000 and 40,000 years ago.

Most researchers now reject the older idea that early humans deliberately captured wolf pups and raised them. Instead, many favor a theory of self-domestication. According to this view, some wolves began to scavenge near human camps, where discarded bones and scraps offered an easy meal. Wolves that were less fearful of people gained a reliable food source and were more likely to survive and reproduce.

Over many generations, this tolerance became an inherited trait, and the animals gradually changed in appearance as well as behavior: their skulls became smaller, their teeth less pronounced, and their ears often softer. Humans, in turn, benefited from the arrangement. The animals warned of approaching predators and later assisted with hunting. The relationship was therefore mutually beneficial long before it became intentional.`,
      questions: [
        {
          question: "What is the passage mainly about?",
          options: [
            "How one theory explains the origin of domesticated dogs",
            "Why wolves are dangerous to early humans",
            "Methods that were used to train hunting dogs",
            "Genetic differences between ancient and modern wolves",
          ],
          correct: 0,
        },
        {
          question: "The word \"reliable\" in the passage is closest in meaning to",
          options: ["unusual", "dependable", "healthy", "temporary"],
          correct: 1,
        },
        {
          question: "According to the passage, why did some wolves approach human camps?",
          options: [
            "To find food that people had thrown away",
            "To escape from larger predators",
            "To find shelter during cold winters",
            "To follow humans on hunting trips",
          ],
          correct: 0,
        },
        {
          question: "Which of the following is NOT mentioned as a physical change in early dogs?",
          options: ["Smaller skulls", "Less pronounced teeth", "Softer ears", "Longer legs"],
          correct: 3,
        },
        {
          question: "What can be inferred about the older theory of dog domestication?",
          options: [
            "It was based on recent genetic studies.",
            "It held that humans played an active role from the start.",
            "It claimed that dogs were domesticated after cats.",
            "It is still accepted by most researchers.",
          ],
          correct: 1,
          explanation: "The older idea was that humans \"deliberately captured wolf pups and raised them\" — an active human role.",
        },
      ],
    },
  ],

  // ── Harder Module 2 ──────────────────────────────────────────
  upper: [
    {
      id: "up-words",
      type: "words",
      title: "Coral Reefs",
      text: "Coral reefs cover less than one percent of the ocean floor, yet they support roughly a quarter of all marine species. Their [[remarkable]] diversity depends [[on]] a delicate partnership [[between]] coral animals and [[microscopic]] algae that live [[inside]] their tissues. The [[algae]] supply food through [[photosynthesis]], while the [[coral]] provides shelter. When water [[temperatures]] rise too far, the corals [[expel]] the algae, a process known as bleaching.",
    },
    {
      id: "up-daily",
      type: "daily",
      title: "Lease Renewal Letter",
      text: `Dear Resident,

Your lease for Unit 4B at Maple Court expires on August 31. If you intend to renew, please sign and return the enclosed agreement by July 15.

Renewals received after that date cannot be guaranteed the current rate; monthly rent for new agreements will increase by 4% to reflect rising maintenance costs. Residents who renew on time will keep their current rent for an additional twelve months.

If you plan to move out, written notice is required by the same date so that we can begin showing the unit to prospective tenants.

Sincerely,
Maple Court Management`,
      questions: [
        {
          question: "What will most likely happen if a resident returns the agreement on August 1?",
          options: [
            "Their lease will end immediately.",
            "They may have to pay a higher rent.",
            "They will receive a 4% discount.",
            "Their unit will be shown to new tenants.",
          ],
          correct: 1,
        },
        {
          question: "Why does management need notice from residents who are leaving?",
          options: [
            "To calculate the residents' final payment",
            "To schedule repairs before August",
            "To start presenting the apartment to others",
            "To return the residents' deposits on time",
          ],
          correct: 2,
        },
        {
          question: "The word \"prospective\" in the letter is closest in meaning to",
          options: ["current", "potential", "wealthy", "former"],
          correct: 1,
        },
      ],
    },
    {
      id: "up-academic",
      type: "academic",
      title: "Urban Heat Islands",
      text: `Cities are often several degrees warmer than the surrounding countryside, a phenomenon known as the urban heat island effect. The difference is most pronounced at night. During the day, asphalt, concrete, and brick absorb large amounts of solar radiation; after sunset, these materials release the stored heat slowly, keeping urban air warm long after rural areas have cooled.

The geometry of cities compounds the problem. Tall buildings lining narrow streets trap outgoing heat and block winds that would otherwise carry it away. Human activity contributes as well, since vehicles, air conditioners, and industrial processes all emit waste heat.

The consequences extend beyond discomfort. Higher temperatures increase demand for electricity, which in turn raises emissions from power plants, and prolonged heat waves pose serious risks to elderly residents. Planners have responded with a range of strategies. Reflective "cool roofs" reduce the amount of energy that buildings absorb, while trees and green spaces provide shade and cool the air through evaporation. Such measures cannot eliminate the effect entirely, but studies indicate they can lower peak urban temperatures significantly.`,
      questions: [
        {
          question: "According to the passage, why is the urban heat island effect strongest at night?",
          options: [
            "Winds are weaker in cities after sunset.",
            "Building materials gradually release heat absorbed during the day.",
            "Air conditioners are used more at night.",
            "Rural areas absorb less solar radiation.",
          ],
          correct: 1,
        },
        {
          question: "The word \"compounds\" in the passage is closest in meaning to",
          options: ["explains", "worsens", "measures", "reduces"],
          correct: 1,
        },
        {
          question: "Why does the author mention electricity demand?",
          options: [
            "To show that the effect has consequences beyond personal comfort",
            "To argue that power plants cause urban heat islands",
            "To compare energy use in cities and rural areas",
            "To explain how cool roofs are manufactured",
          ],
          correct: 0,
        },
        {
          question: "Which of the following is NOT mentioned as a source of waste heat?",
          options: ["Vehicles", "Air conditioners", "Industrial processes", "Street lighting"],
          correct: 3,
        },
        {
          question: "What can be inferred about cool roofs and green spaces?",
          options: [
            "They are too expensive for most cities.",
            "They work only during the daytime.",
            "They reduce, but do not remove, the temperature difference.",
            "They have replaced all other planning strategies.",
          ],
          correct: 2,
          explanation: "The passage says such measures \"cannot eliminate the effect entirely\" but can lower peak temperatures.",
        },
      ],
    },
  ],

  // ── Easier Module 2 ──────────────────────────────────────────
  lower: [
    {
      id: "low-words",
      type: "words",
      title: "Home Gardens",
      text: "Many people enjoy keeping a small garden at home. Growing [[your]] own vegetables [[can]] save money and [[provide]] fresh food [[during]] the summer. Gardening [[is]] also good [[exercise]], and many [[people]] find it [[relaxing]] after a [[long]] day at [[work]].",
    },
    {
      id: "low-daily",
      type: "daily",
      title: "Café Specials",
      text: `CAMPUS CAFÉ — WEEKLY SPECIALS

Monday ........ Tomato soup with bread — $4.50
Wednesday ..... Chicken salad sandwich — $6.00
Friday ........ Vegetable pasta — $5.50

Students who show a valid ID receive 10% off all specials.
Specials are served from 11 a.m. until they run out.
Hot drinks are half price after 3 p.m. every day.`,
      questions: [
        {
          question: "Which special is the least expensive?",
          options: ["Tomato soup with bread", "Chicken salad sandwich", "Vegetable pasta", "They all cost the same."],
          correct: 0,
        },
        {
          question: "What must students do to get a discount on the specials?",
          options: ["Arrive before 11 a.m.", "Show their student ID", "Buy a hot drink", "Order on Wednesday"],
          correct: 1,
        },
        {
          question: "What is true about hot drinks?",
          options: [
            "They are free with every special.",
            "They are served only on Fridays.",
            "They cost less in the late afternoon.",
            "They are sold until 3 p.m.",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "low-academic",
      type: "academic",
      title: "How Volcanoes Form",
      text: `A volcano is an opening in Earth's surface through which melted rock, gas, and ash escape. Deep underground, temperatures are high enough to melt rock, forming a material called magma. Because magma is lighter than the solid rock around it, it slowly rises toward the surface and collects in large pools called magma chambers. When pressure in a chamber becomes too great, the magma pushes through cracks in the rock and erupts. Once it reaches the surface, it is called lava.

Most volcanoes are found where Earth's tectonic plates meet. Where two plates move apart, as in the middle of the Atlantic Ocean, magma rises to fill the gap. Where one plate slides beneath another, the sinking plate melts and feeds volcanoes above it. A few volcanoes, such as those in Hawaii, form far from plate boundaries, above unusually hot areas of the mantle called hot spots.

Although eruptions can be destructive, volcanic soil is often very fertile, which is why many farming communities live near volcanoes.`,
      questions: [
        {
          question: "What is the passage mainly about?",
          options: [
            "How and where volcanoes form",
            "The history of famous eruptions",
            "How scientists predict eruptions",
            "Farming methods in Hawaii",
          ],
          correct: 0,
        },
        {
          question: "Why does magma rise toward the surface?",
          options: [
            "It is pushed up by ocean water.",
            "It is lighter than the surrounding rock.",
            "It cools down underground.",
            "It is pulled up by tectonic plates.",
          ],
          correct: 1,
        },
        {
          question: "According to the passage, what is lava?",
          options: [
            "Rock that has never melted",
            "Gas that escapes from a volcano",
            "Magma that has reached Earth's surface",
            "Ash that falls after an eruption",
          ],
          correct: 2,
        },
        {
          question: "According to the passage, the volcanoes in Hawaii are unusual because they",
          options: [
            "erupt more often than other volcanoes",
            "are not located near a plate boundary",
            "are found in the Atlantic Ocean",
            "do not produce any lava",
          ],
          correct: 1,
        },
        {
          question: "Why do many farmers live near volcanoes?",
          options: [
            "The land there is cheaper.",
            "The weather there is warmer.",
            "The soil there is good for growing crops.",
            "Volcanoes provide water for their fields.",
          ],
          correct: 2,
        },
      ],
    },
  ],
};

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}
